import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';

/**
 * Builds the "Choose …" label shown when an option is still unselected (e.g. "Choose Size").
 * Mirrors buildChooseOptionLabel() in product-form.js so the sticky bar CTA matches the
 * product info CTA exactly. Strips a leading "Choose " from the option name to avoid
 * "Choose Choose Color" when the admin option is already named "Choose Color".
 *
 * @param {string | undefined} optionName - The unselected option's display name.
 * @param {any} themeTranslations - window.Theme.translations object.
 * @returns {string}
 */
function buildChooseOptionLabel(optionName, themeTranslations) {
  if (optionName) {
    const template = themeTranslations?.products?.product?.choose_specific_option || 'Choose {option}';
    const cleanName = optionName.replace(/^choose\s+/i, '').trim() || optionName;
    return template.replace('{option}', cleanName);
  }
  return themeTranslations?.products?.product?.choose_options || 'Choose options';
}

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.#getInitialQuantity();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    // In themes migrated from 2.0, the footer element doesn't exist
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    // Observer for buy buttons visibility
    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      // Show sticky bar whenever buy buttons are not visible (above or below viewport)
      if (!entry.isIntersecting && !this.#isStuck) {
        this.#showStickyBar();
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    // Observer for footer visibility - hides sticky bar at page bottom
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          // Footer out of view - show sticky bar if buy buttons are not visible
          const rect = buyButtonsBlock.getBoundingClientRect();
          const isVisible = rect.top >= 0 && rect.bottom <= window.innerHeight;
          this.#hiddenByBottom = false;
          if (!isVisible) {
            this.#showStickyBar();
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    if (!this.#targetAddToCartButton) return;

    // When the product still needs an option chosen, the product info CTA reads "Choose Size" /
    // "Choose Color" and its button is disabled. Clicking a disabled button does nothing, so
    // instead mirror the product info behaviour and scroll up to the variant picker so the
    // customer can make a selection.
    if (this.dataset.requiresSelection === 'true' || this.#targetAddToCartButton.disabled) {
      this.#scrollToProductInfo();
      return;
    }

    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) return;
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
    const sourceStyles = getComputedStyle(this.refs.productImage);

    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    // Combined listings swap the underlying product; adopt the new id (mirrors product-form.js)
    // so the productId guard below doesn't skip the update after a child-product switch.
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const variant = event.detail.resource;

    // Morph the sticky bar from the server response when it's included. Combined-listing
    // switches and the initial no-preselect dispatch send an empty document (the section is
    // re-rendered elsewhere), so this is optional — the selection-state sync below still runs.
    const newStickyAddToCart = event.detail.data.html?.querySelector('sticky-add-to-cart');
    const newStickyBar = newStickyAddToCart?.querySelector('[ref="stickyBar"]');
    if (newStickyBar) {
      // Store current visibility state before morphing
      const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
      const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

      // Morph the entire sticky bar content
      morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

      // Restore visibility state after morphing
      this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
      if (variantAvailable != null) this.dataset.variantAvailable = variantAvailable;
    }

    // Update the dataset attributes with new variant info
    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    // Re-cache the target add to cart button after morphing
    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    }

    if (variant == null) {
      this.#handleVariantUnavailable();
    }

    // Mirror the product info CTA: when no variant is selected yet, show "Choose Size" /
    // "Choose Color" and route clicks to the picker instead of trying to add to cart.
    this.#updateSelectionState(event.detail);

    // Restore the current quantity display if needed
    this.#updateButtonText();
  };

  /**
   * Handles variant selected events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    // The variant update event will follow and handle all updates via morph
    // We just update the dataset here for tracking
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    // Show only the options the customer has actually chosen — never an unselected size/colour
    // left over from the server-rendered first variant. Clear it entirely when nothing is chosen.
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Reflects whether the product still needs an option selected. When it does, the sticky CTA
   * matches the product info CTA ("Choose Size" / "Choose Color"). Derived from the same
   * VariantUpdateEvent detail the product form uses, so the two CTAs always stay in sync.
   *
   * @param {{ resource: ProductVariant | null, data: { firstUnselectedOptionName?: string } }} detail
   */
  #updateSelectionState(detail) {
    const requiresSelection = detail.resource == null;
    // @ts-expect-error - Theme is a global injected by the theme runtime
    const themeTranslations = window.Theme?.translations;

    if (requiresSelection) {
      this.dataset.requiresSelection = 'true';
      this.#setButtonLabel(buildChooseOptionLabel(detail.data?.firstUnselectedOptionName, themeTranslations));
    } else if (this.dataset.requiresSelection === 'true') {
      // Leaving the choose-options state without a server morph (e.g. a combined-listing switch
      // that auto-resolved to a single available variant). Restore the standard label.
      this.dataset.requiresSelection = 'false';
      this.#setButtonLabel(
        themeTranslations?.products?.product?.add_to_cart || themeTranslations?.actions?.add_to_cart || 'Add to bag'
      );
    } else {
      this.dataset.requiresSelection = 'false';
    }
  }

  /**
   * Sets the visible label text on the sticky add to cart button without disturbing the
   * sibling quantity-display span.
   * @param {string} text
   */
  #setButtonLabel(text) {
    const labelElement = this.refs.addToCartButton.querySelector('.add-to-cart-text__content > span:first-child');
    if (labelElement) labelElement.textContent = text;
  }

  /**
   * Scrolls the customer up to the variant picker so they can make a selection.
   */
  #scrollToProductInfo() {
    const section = this.closest('.shopify-section');
    const target =
      section?.querySelector('variant-picker') ||
      section?.querySelector('.buy-buttons-block') ||
      this.#getProductForm();
    if (!(target instanceof HTMLElement)) return;
    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CustomEvent} _event - The cart event (unused)
   */
  #handleCartAddComplete = (_event) => {
    // Reset the puppet flag after cart operation
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  // Helper methods
  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    // Hide the quantity in the "Choose Size" state — there is no variant to add yet.
    const available = !addToCartButton.disabled && this.dataset.requiresSelection !== 'true';

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
